import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import LoadingOverlay from "./LoadingOverlay";
import ConfirmDialog from "./ConfirmDialog";
import MaterialLocationModal from "./modals/MaterialLocationModal";

const lbl =
  "block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1";
const inp =
  "w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-cyan-400";
const thCls =
  "px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide whitespace-nowrap border-r last:border-r-0";
const tdCls =
  "px-4 py-2.5 text-xs text-gray-700 border-r last:border-r-0 whitespace-nowrap";

function NewMaterialLocation() {
  const [locationName, setLocationName] = useState("");
  const [locationNameList, setLocationNameList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [uploadPending, setUploadPending] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState(null);

  useEffect(() => {
    getLocationNameList();
  }, []);

  const getLocationNameList = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(
        "https://eps.elkomprintama.com/get/api/store/location",
      );
      setLocationNameList(res.data.result);
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Failed to load locations.");
    } finally {
      setIsLoading(false);
    }
  };

  const insertLocationName = async () => {
    setIsLoading(true);
    try {
      const res = await axios.post(
        "https://eps.elkomprintama.com/post/api/store/location",
        {
          location_name: locationName.toUpperCase(),
        },
      );
      if (res.status === 200) {
        toast.success(res.data.msg);
        getLocationNameList();
        setLocationName("");
      }
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Insert failed.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!locationName) {
      toast.warning("Please fill in the location name.");
      return;
    }
    setUploadPending(true);
    setShowConfirm(true);
  };

  return (
    <div className="flex flex-col" style={{ maxHeight: "calc(100vh - 80px)" }}>
      {isLoading && <LoadingOverlay message="Loading…" />}

      {/* Header */}
      <div className="w-full rounded-lg bg-cyan-700 mb-4 flex-shrink-0">
        <label className="block uppercase text-white tracking-wide text-5xl text-center font-bold py-2">
          New Material Location
        </label>
      </div>

      {/* Body card */}
      <div className="flex-1 min-h-0 flex flex-col bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="flex-shrink-0 px-6 py-5 border-b border-gray-100"
        >
          <div>
            <label className={lbl}>Location Name</label>
            <input
              className={inp}
              name="locationname" type="text"
              placeholder="e.g. WAREHOUSE A"
              value={locationName.toUpperCase()}
              onChange={(e) => setLocationName(e.target.value)}
            />
          </div>
          <button
            type="submit"
            className="mt-4 w-full py-2.5 rounded-xl text-sm font-bold text-white bg-cyan-700 hover:bg-cyan-800 transition"
          >
            Add Location
          </button>
          <p className="text-xs text-gray-400 mt-2">
            Double-click a row to edit it.
          </p>
        </form>

        {/* Table */}
        <div className="flex-1 min-h-0 overflow-auto">
          <table className="w-full text-xs border-collapse">
            <thead className="sticky top-0 bg-gray-700 text-white uppercase">
              <tr>
                <th className={thCls}>Location ID</th>
                <th className={thCls}>Location Name</th>
              </tr>
            </thead>
            <tbody>
              {locationNameList.length === 0 && !isLoading ? (
                <tr>
                  <td
                    colSpan={2}
                    className="py-12 text-center text-gray-400 italic text-sm"
                  >
                    No locations found.
                  </td>
                </tr>
              ) : (
                locationNameList.map((data, i) => (
                  <tr
                    key={data.product_location_id}
                    onDoubleClick={() => {
                      setShowModal(true);
                      setModalData(data);
                    }}
                    className={`border-b cursor-pointer hover:bg-cyan-50 ${i % 2 === 0 ? "bg-gray-50" : "bg-white"}`}
                  >
                    <td className={`${tdCls} text-gray-400`}>
                      {data.product_location_id}
                    </td>
                    <td className={`${tdCls} font-medium text-gray-800`}>
                      {data.product_location_name}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer count */}
        {locationNameList.length > 0 && (
          <div className="flex-shrink-0 px-5 py-2.5 border-t border-gray-100 bg-gray-50">
            <span className="text-xs text-gray-400">
              {locationNameList.length} location
              {locationNameList.length !== 1 ? "s" : ""}
            </span>
          </div>
        )}
      </div>

      {showConfirm && uploadPending && (
        <ConfirmDialog
          message="Are you sure you want to insert this location?"
          onConfirm={() => {
            setShowConfirm(false);
            insertLocationName();
            setUploadPending(false);
          }}
          onCancel={() => {
            setShowConfirm(false);
            setUploadPending(false);
          }}
        />
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
          <MaterialLocationModal
            dataSet={modalData}
            onClose={() => setShowModal(false)}
            refreshData={getLocationNameList}
          />
        </div>
      )}
    </div>
  );
}

export default NewMaterialLocation;
